import { createBrowserRouter, Navigate } from 'react-router-dom';
// Critical path (auth, profiles, and the public directory) stays in the entry chunk. Import order is
// kept as-is because it determines the order of component stylesheets in the CSS bundle.
import Discover from '../pages/Discover';
import NotFound404 from '../pages/NotFound404';
import Profile from '../pages/Profile';
import DirectoryHub from '../pages/Directory';
import ProtectedRoute, { PublicProfileGate } from './ProtectedRoute';
import Layout from '../components/layout/Layout';
import BrowseLayout from '../components/layout/BrowseLayout';
import { StandalonePage, lazyRoute } from './lazyRoute';
import { syncRouteTitle } from '../utils/documentTitle';
import { syncNightMode } from '../theme/nightMode';

// Everything below is loaded on demand. Lazy routes inside <ProtectedRoute /> get their
// Suspense boundary from the shell; standalone routes are wrapped in <StandalonePage />.
const Login = lazyRoute(() => import('../pages/Login'));
const Project = lazyRoute(() => import('../pages/Project'));
const Marketplace = lazyRoute(() => import('../pages/Marketplace'));
const CreateJamSession = lazyRoute(() => import('../pages/CreateJamSession'));
const Terms = lazyRoute(() => import('../pages/Onboarding/Terms'));
const SignupEntry = lazyRoute(() => import('../pages/Onboarding/SignupEntry'));
const DeleteAccountPage = lazyRoute(() => import('../pages/Settings/DeleteAccountPage'));
const SuggestedPeople = lazyRoute(() => import('../pages/Onboarding/SuggestedPeople'));
const Links = lazyRoute(() => import('../pages/Links'));
const ProfileSearchResults = lazyRoute(() => import('../pages/ProfileSearchResults'));
const Liked = lazyRoute(() => import('../pages/Liked'));
const PrivacyPolicy = lazyRoute(() => import('../pages/PrivacyPolicy'));
const TermsAndConditions = lazyRoute(() => import('../pages/TermsAndConditions'));
const AdminUserLookup = lazyRoute(() => import('../pages/AdminUserLookup'));
const AdminLogs = lazyRoute(() => import('../pages/AdminLogs'));
const AdminDashboard = lazyRoute(() => import('../pages/AdminDashboard'));
const AdminDepartures = lazyRoute(() => import('../pages/AdminDepartures'));
const AdminBoosts = lazyRoute(() => import('../pages/AdminBoosts'));
const AdminAnnounce = lazyRoute(() => import('../pages/AdminAnnounce'));
const AdminUserMod = lazyRoute(() => import('../pages/AdminUserMod'));
const Jail = lazyRoute(() => import('../pages/Jail'));
const AdminPromos = lazyRoute(() => import('../pages/AdminPromos'));
const PartnerDashboard = lazyRoute(() => import('../pages/PartnerDashboard'));
const PartnerPromos = lazyRoute(() => import('../pages/PartnerPromos'));
const PartnerBilling = lazyRoute(() => import('../pages/PartnerBilling'));
const CollaborationsPage = lazyRoute(() => import('../pages/CollaborationsPage'));
const Notifications = lazyRoute(() => import('../pages/Notifications'));
const LinkRepair = lazyRoute(
    // @ts-expect-error -- legacy untyped JSX module
    () => import('../components/admin/LinkRepair.jsx'),
);
const ReferralMap = lazyRoute(() => import('../pages/ReferralMap'));
const JamBoard = lazyRoute(() => import('../pages/JamBoard'));
const Settings = lazyRoute(() => import('../pages/Settings'));
const PaymentSuccess = lazyRoute(() => import('../pages/Payment/Success'));
const PaymentCancel = lazyRoute(() => import('../pages/Payment/Cancel'));
const directoryElement = (
    <PublicProfileGate>
        <Layout>
            <DirectoryHub />
        </Layout>
    </PublicProfileGate>
);

// Reachable signed in or out. It sits outside <ProtectedRoute /> so a mistyped link
// shows a 404 instead of bouncing a visitor to the login screen.
const notFoundElement = (
    <PublicProfileGate>
        <Layout>
            <NotFound404 />
        </Layout>
    </PublicProfileGate>
);

const router = createBrowserRouter([
    {
        path: '/login',
        element: (
            <StandalonePage>
                <Login />
            </StandalonePage>
        ),
    },
    {
        path: '/login/success',
        element: (
            <StandalonePage>
                <Login />
            </StandalonePage>
        ),
    },
    {
        path: '/login/error',
        element: (
            <StandalonePage>
                <Login />
            </StandalonePage>
        ),
    },
    {
        path: '/jail',
        element: (
            <StandalonePage>
                <Jail />
            </StandalonePage>
        ),
    },
    {
        path: '/signup',
        element: (
            <StandalonePage>
                <SignupEntry />
            </StandalonePage>
        ),
    },
    {
        path: '/account/delete',
        element: (
            <StandalonePage>
                <DeleteAccountPage />
            </StandalonePage>
        ),
    },
    {
        path: '/signup/success',
        element: (
            <StandalonePage>
                <Login isSignup={true} />
            </StandalonePage>
        ),
    },
    {
        path: '/signup/error',
        element: (
            <StandalonePage>
                <Login isSignup={true} />
            </StandalonePage>
        ),
    },
    {
        path: '/register',
        element: (
            <StandalonePage>
                <SignupEntry />
            </StandalonePage>
        ),
    },
    {
        path: '/signup/terms',
        element: (
            <StandalonePage>
                <Terms />
            </StandalonePage>
        ),
    },
    {
        path: '/onboarding/people',
        element: <Navigate to="/people" replace />,
    },
    {
        path: '/onboarding',
        element: <Navigate to="/signup" replace />,
    },
    {
        path: '/profile-completion',
        element: <Navigate to="/signup" replace />,
    },
    {
        path: '/privacy-policy',
        element: (
            <StandalonePage>
                <PrivacyPolicy />
            </StandalonePage>
        ),
    },
    {
        path: '/terms',
        element: (
            <StandalonePage>
                <TermsAndConditions />
            </StandalonePage>
        ),
    },
    {
        path: '/directory/:skillSlug?/:placeSlug?',
        element: directoryElement,
    },
    {
        path: '/404',
        element: notFoundElement,
    },
    {
        path: '/:username',
        element: (
            <PublicProfileGate>
                <Layout>
                    <Profile />
                </Layout>
            </PublicProfileGate>
        ),
    },
    {
        path: '/:username/links',
        element: (
            <PublicProfileGate>
                <StandalonePage>
                    <Links />
                </StandalonePage>
            </PublicProfileGate>
        ),
    },
    {
        path: '/',
        element: <ProtectedRoute />,
        children: [
            {
                element: <BrowseLayout />,
                children: [
                    {
                        path: '',
                        element: <Discover />,
                    },
                    {
                        path: 'discover',
                        element: <Discover />,
                    },
                    {
                        path: 'liked',
                        element: <Liked />,
                    },
                    {
                        path: 'search',
                        element: <ProfileSearchResults />,
                    },
                    {
                        path: 'jamboard',
                        element: <JamBoard />,
                    },
                    {
                        path: 'collaborations',
                        element: <CollaborationsPage />,
                    },
                ],
            },
            {
                path: 'profile',
                element: <Profile />,
            },
            {
                path: 'settings',
                element: <Settings />,
            },
            {
                path: 'people',
                element: <SuggestedPeople />,
            },
            {
                path: 'projects/:projectId',
                element: <Project />,
            },
            {
                path: 'notifications',
                element: <Notifications />,
            },
            {
                path: 'marketplace',
                element: <Marketplace />,
            },
            {
                path: 'bell',
            },
            {
                path: 'create-jam-session',
                element: <CreateJamSession />,
            },
            {
                path: 'admin',
                element: <AdminDashboard />,
            },
            {
                path: 'admin/departures',
                element: <AdminDepartures />,
            },
            {
                path: 'admin/user-lookup',
                element: <AdminUserLookup />,
            },
            {
                path: 'admin/user-mod',
                element: <AdminUserMod />,
            },
            {
                path: 'admin/logs',
                element: <AdminLogs />,
            },
            {
                path: 'admin/boosts',
                element: <AdminBoosts />,
            },
            {
                path: 'admin/announce',
                element: <AdminAnnounce />,
            },
            {
                path: 'admin/link-repair',
                element: <LinkRepair />,
            },
            {
                path: 'admin/referral-map',
                element: <ReferralMap />,
            },
            {
                path: 'admin/promos',
                element: <AdminPromos />,
            },
            {
                path: 'partner',
                element: <PartnerDashboard />,
            },
            {
                path: 'partner/promos',
                element: <PartnerPromos />,
            },
            {
                path: 'partner/billing',
                element: <PartnerBilling />,
            },
            {
                path: '/payment/success',
                element: <PaymentSuccess />,
            },
            {
                path: '/payment/cancel',
                element: <PaymentCancel />,
            },
        ],
    },
    {
        path: '*',
        element: notFoundElement,
    },
]);

if (typeof window !== 'undefined') {
    syncRouteTitle(window.location.pathname, window.location.search);
    syncNightMode(window.location.pathname);
    router.subscribe((state) => {
        syncRouteTitle(state.location.pathname, state.location.search);
        syncNightMode(state.location.pathname);
    });
}

export default router;
